import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import Spinner from '../common/spinner';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../routes';
import { getHeaders, handleErrorResponse } from '../../lib/requests';
import PaginationControl from '../common/pagination_control';
import GeneralButton from '../common/button_general';
import TableHeader from '../common/table_header';
import GeneralizedTableRow from '../common/table_row';
import { PencilSquareIcon, LinkIcon, TrashIcon } from '@heroicons/react/24/outline';
import usePaginationAndSorting from '../../hooks/usepaginationandsorting';
import NoResultsFound from '../common/noresultsfound';

function ProviderList() {
  const { page, pageSize, sortBy, handlePageChange, handleSortClick } = usePaginationAndSorting('asc_name');
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchQueryParam = queryParams.get('search');
  const [searchQuery] = useState(searchQueryParam || '');

  const formatDeliveryDays = (value) => {
    if (!value) return '';
    const dayMap = { 0: "Su", 1: "M", 2: "Tu", 3: "W", 4: "Th", 5: "F", 6: "Sa" };
    if (Array.isArray(value)) {
      return value.map(day => dayMap[day] || day).join(', ');
    }
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map(day => dayMap[day] || day).join(', ');
      }
    } catch (err) {
      return;
    }
    return value;
  };

  useEffect(() => {
    const fetchClients = async () => {
      if (!pageSize) return;
      setIsLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const apiToken = localStorage.getItem('accessToken');
        const response = await axios.get(`${apiUrl}/clients`, {
          headers: getHeaders(apiToken),
          params: { page, pageSize, ...(sortBy && { order_by: sortBy }), ...(searchQuery && { search: searchQuery }) },
        });
        setTotalPages(response.data.totalPages);
        setTotalRecords(response.data.total);
        setClients(response.data.data);
      } catch (error) {
        handleErrorResponse(error, navigate, setShowError, setErrorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchClients();
  }, [page, pageSize, sortBy, searchQuery]);

  const handleDelete = async (client) => {
    const confirmed = window.confirm(`Are you sure you want to delete provider ${client.name}?`);
    if (!confirmed) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const apiToken = localStorage.getItem('accessToken');
      await axios.delete(`${apiUrl}/clients/${client.client_id}`, {
        headers: getHeaders(apiToken)
      });
      setClients(prev => prev.filter(c => c.client_id !== client.client_id));
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    }
  };

  const currentRoute = routes.find(route => route.path === location.pathname);
  const routePageTitle = currentRoute ? currentRoute.pageTitle : 'Providers';

  const columns = [
    { id: 'simple_id', name: 'ID' },
    { id: 'name', name: 'Name' },
    { id: 'street', name: 'Street' },
    { id: 'city', name: 'City' },
    { id: 'state', name: 'State' },
    { id: 'zip_code', name: 'Zip' },
    { id: 'primary_contact_name', name: 'Primary Contact' },
    { id: 'phone', name: 'Phone Number' },
    { id: 'delivery_days', name: 'Delivery Days', format: (value) => formatDeliveryDays(value) },
    { id: 'register_link', name: '', link: (rowData) => `/register?client_id=${rowData.client_id}`, tooltip: 'Registration Link (right-click to copy)', icon: LinkIcon },
    { id: 'edit', name: '', link: (rowData) => `/providers/${rowData.client_id}`, tooltip: 'Edit Provider', icon: PencilSquareIcon },
    { id: 'delete', name: '', tooltip: 'Delete Provider', render: (rowData) => (
      <button onClick={() => handleDelete(rowData)} title="Delete Provider">
        <TrashIcon className="h-5 w-5 text-red-500" />
      </button>
    ) }
  ];

  return (
    <MasterLayout pageTitle={routePageTitle} searchQuery={searchQuery}>
      {showError && <ErrorMessage message={errorMessage} />}
      <div className="px-2 sm:px-3 lg:px-3">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 flex justify-between">
          <h1 className="text-lg font-semibold text-gray-900">Provider List</h1>
          <GeneralButton type="button" buttonType="primary" onClick={() => navigate('/providers/create')}>
            Add Provider
          </GeneralButton>
        </div>
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2">
          {isLoading ? (
            <Spinner minHeight={630} />
          ) : clients.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-300">
              <TableHeader columns={columns} handleSortClick={handleSortClick} currentSort={sortBy} />
              {clients.map((client) => (
                <GeneralizedTableRow key={client.client_id} rowData={client} columns={columns} />
              ))}
            </table>
          ) : (
            <NoResultsFound message={"No " + routePageTitle.toLowerCase() + " found."} minHeight={630} />
          )}
          <PaginationControl
            page={page}
            recordsPerPage={pageSize}
            totalRecords={totalRecords}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />
        </div>
      </div>
    </MasterLayout>
  );
}

export default ProviderList;
